import { GeoJsonLayer, IconLayer } from "@deck.gl/layers";
import type { Feature, FeatureCollection } from "geojson";
import type { PlumeOulines } from "../../../apiClient/generated";
import { MAP_ZOOM_SHOW_DETAILS } from "../constants";

const iconMapping = {
    marker: {
        x: 0,
        y: 0,
        width: 48,
        height: 48,
        mask: false,
    },
};

export const PlumeOutlinesLayer = (
    data: PlumeOulines[],
    name: string = "plume_outlines",
    visible: boolean,
    zoomLevel: number,
    opacity: number,
    detail: boolean = false,
) => {
    if (!data) {
        return [];
    }

    if (!detail && zoomLevel < MAP_ZOOM_SHOW_DETAILS) {
        return [];
    }

    const featureCollection: FeatureCollection = {
        type: "FeatureCollection",
        features: data
            .filter((outline) => !outline.defaultHide || detail)
            .map((outline) => {
                return {
                    type: "Feature",
                    geometry: outline.geometry,
                    properties: {},
                };
            }) as unknown as any,
    };

    const color: [number, number, number] = [23, 122, 212];

    return new GeoJsonLayer({
        id: name,
        data: featureCollection,
        pointType: "line",
        filled: true,
        stroked: true,
        pickable: false,
        visible: visible,
        lineWidthUnits: "pixels",
        getLineWidth: 2,
        getLineColor: [...color, opacity > 0 ? 255 : 0],
        getFillColor: [...color, opacity * 255],
    });
};

export interface EmissionLayerStyle {
    fillColor: [number, number, number, number];
    lineColor: [number, number, number, number];
    lineWidth: number;
    highlightColor: [number, number, number, number];
    groupFillColor?: [number, number, number, number];
    groupLineColor?: [number, number, number, number];
    iconAtlas: string;
    textColor: [number, number, number, number];
}

export const EmissionRecordsLayers = (
    data: Feature[],
    visible: boolean,
    zoomLevel: number,
    layerId: string = "emissions",
    layerStyle: EmissionLayerStyle,
    clusteredData?: Feature[],
    onHover?: (hoverInfo: any) => void,
) => {
    // When zooming in: show only markers.
    if (zoomLevel > MAP_ZOOM_SHOW_DETAILS - 1) {
        return [
            new IconLayer({
                id: `${layerId}-emission-crosshairs`,
                data: data,
                iconAtlas: layerStyle.iconAtlas,
                iconMapping,
                getIcon: () => "marker",
                getPosition: (d) => d.geometry.coordinates,
                getSize: 25,
                alphaCutoff: 0.05,
                highlightColor: layerStyle.highlightColor,
                stroked: true,
                pickable: true,
                visible,
                autoHighlight: true,
                onHover,
            }),
        ];
    }

    // When zoomed out: show clusters + bubbles if clusters are enabled.
    const layers: any[] = [
        new GeoJsonLayer({
            id: `${layerId}-emission-points`,
            data: {
                type: "FeatureCollection",
                features:
                    clusteredData && clusteredData.length > 0
                        ? clusteredData.filter((i) => !i.properties.cluster)
                        : data,
            },
            pointType: "circle",
            filled: true,
            getPointRadius: (i) => i.properties.mapDotSize / 2,
            pointRadiusUnits: "pixels",
            lineWidthUnits: "pixels",
            stroked: true,
            pickable: true,
            visible,
            autoHighlight: true,
            onHover,
            highlightColor: layerStyle.highlightColor,
            getFillColor: layerStyle.fillColor,
            getLineWidth: layerStyle.lineWidth,
            getLineColor: layerStyle.lineColor,
        }),
    ];

    if (clusteredData) {
        layers.push(
            new GeoJsonLayer({
                id: `${layerId}-emission-clusters`,
                data: {
                    type: "FeatureCollection",
                    features: clusteredData.filter((i) => i.properties.cluster),
                },
                pointType: "circle+text",
                filled: true,
                getPointRadius: (i) => i.properties.mapDotSize / 2,
                pointRadiusUnits: "pixels",
                lineWidthUnits: "pixels",
                stroked: true,
                pickable: true,
                visible,
                autoHighlight: true,
                getText: (i) => `${i.properties.point_count_abbreviated}`,
                getTextSize: 12,
                getTextColor: [255, 255, 255, 255],
                onHover,
                highlightColor: layerStyle.highlightColor,
                getFillColor: layerStyle.groupFillColor || layerStyle.fillColor,
                getLineWidth: layerStyle.lineWidth,
                getLineColor: layerStyle.groupLineColor || layerStyle.lineColor,
            }),
        );
    }

    // Return layer list.
    return layers;
};
